import { constants } from '../constants/constants';

const calculateTotalDiscount = (order) => {
  return order.m_discount_amount ? parseFloat(order.m_discount_amount)?.toFixed(2) : '0.00';
}

const calculateItemDiscount = (item) => {
  const type = item?.m_discount_type;

  const fieldKey = constants.discountTypes.percentage === type
    ? 'm_discount_percentage'
    : 'm_discount_amount';

  let totalCondimentsDiscount = (item?.condiments ?? []).reduce(
    (accumulator, currentCondiment) => accumulator + currentCondiment?.[fieldKey] ?? 0,
    0,
  );

  if (Number.isNaN(totalCondimentsDiscount)) {
    totalCondimentsDiscount = 0;
  }

  const totalDiscount = parseFloat(totalCondimentsDiscount) + parseFloat(item?.[fieldKey]);

  return totalDiscount ? totalDiscount?.toFixed(2) : '0.00';
}

const calculateTotalItemsDiscount = (order) => {
  const total = order.items.reduce(
    (accumulator, orderItem) => {
      return accumulator + parseFloat(calculateItemDiscountInAmount(orderItem));
    },
    0,
  );

  return total ? total?.toFixed(2) : '0.00';
}

const calculateItemDiscountInAmount = (orderItem) => {
  let totalCondimentsDiscount = (orderItem.condiments ?? []).reduce(
    (accumulator, currentCondiment) => {
      return accumulator + currentCondiment.m_discount_amount;
    },
    0,
  );

  if (Number.isNaN(totalCondimentsDiscount)) {
    totalCondimentsDiscount = 0;
  }

  const canShowDiscount = Boolean(
    totalCondimentsDiscount || orderItem.m_discount_amount,
  );

  if (canShowDiscount) {
    return parseFloat(
      totalCondimentsDiscount + orderItem.m_discount_amount,
    ).toFixed(2);
  }

  return '0.00';
}

const needShowDrinkLimitModal = (
  payers,
  currentCount,
  limitAlcoholPerDay,
) => {
  let minAlcoholDrinksAvailable = limitAlcoholPerDay;

  const hasSystemAccountPayer = payers?.some(
    (payer) => payer?.person_type === 'A',
  );

  if (hasSystemAccountPayer) {
    return false;
  }

  payers?.forEach((payer) => {
    minAlcoholDrinksAvailable = Math.min(
      minAlcoholDrinksAvailable,
      payer?.alcohol_count_per_day_available ?? 0,
    );
  });

  return minAlcoholDrinksAvailable - currentCount < 0;
}

const needShowDrinkTimeLimitModal = (item, order) => {
  if (1 !== parseInt(item?.legal_age_class)) {
    return false;
  }

  return order?.payers?.some((payer) => {
    if (!payer?.alcohol_restricted_until) {
      return false;
    }

    const payerAlcoholRestrictedUntil = new Date(
      payer?.alcohol_restricted_until,
    ).getTime();

    const currentTime = new Date().getTime();

    return payerAlcoholRestrictedUntil > currentTime;
  });
}

const getPayerAlcoholRestrictedDate = (order) => {
  let maxTime = 0;
  let date = '';

  order?.payers?.forEach((payer) => {
    const time = Math.max(
      maxTime,
      new Date(payer?.alcohol_restricted_until).getTime() ?? 0,
    );

    if (time > maxTime) {
      maxTime = time;
      date = payer?.alcohol_restricted_until;
    }
  });

  return date;
}

const isTruthyFlag = (value) => {
  return true === value || 1 === Number(value);
}

const isUpsell = (item) => {
  return isTruthyFlag(item?.isUpsell)
    || isTruthyFlag(item?.is_upsell)
    || isTruthyFlag(item?.menu_item?.is_upsell)
    || isTruthyFlag(item?.menu_item_condiment?.is_upsell)
    || isTruthyFlag(
      item?.menu_item_condiment?.menu_item?.is_upsell,
    );
}

const getUpsellTitle = (item) => {
  return item?.upsellTitle
    || item?.upsell_title
    || item?.menu_item?.upsell_title
    || item?.menu_item_condiment?.upsell_title
    || item?.menu_item_condiment?.menu_item?.upsell_title
    || null;
}

const hasSelectedUpsellCondiment = (condiment) => {
  return condiment?.items?.some(
    (item) => item.checked && isUpsell(item),
  ) ?? false;
}

const getRequiredCondiments = (condiments = []) => {
  return (condiments ?? []).filter((condiment) => {
    return 1 === Number(condiment?.menu_item_condiment?.type);
  });
}

const getDisplayedCondiments = (condiments = []) => {
  return (condiments ?? []).filter((condiment) => {
    const type = Number(condiment?.menu_item_condiment?.type);

    return 1 === type || isUpsell(condiment);
  });
}

const addItemToOrder = (
  count,
  order,
  orderItem,
  limitAlcoholPerDay,
) => {
  order?.items?.forEach((item) => {
    if (item?.legal_age_class === 1) {
      count += item.count;
    }
  });

  if (
    orderItem?.legal_age_class === 1
    && needShowDrinkLimitModal(
      order.payers,
      count,
      limitAlcoholPerDay,
    )
  ) {
    return {
      status: false,
      emmit: {
        type: 'showConfirm',
        event: 'drinkLimit',
      },
    };
  }

  if (
    orderItem?.legal_age_class === 1
    && needShowDrinkTimeLimitModal(orderItem, order)
  ) {
    const restrictedUntil = getPayerAlcoholRestrictedDate(order);

    return {
      status: false,
      emmit: {
        type: 'showTimer',
        event: {
          type: 'alcoholDrinkLimit',
          restrictedUntil,
        },
      },
    };
  }

  return {
    status: true,
  };
}

const getUserSelectType = (additionModeId) => {
  return 1 === additionModeId
    ? 'default'
    : 'barcode';
}

const getButtonTipTitle = (order) => {
  const tipValue = order?.gratuity_type === constants.gratuityTypes.amount
    ? (
      order?.gratuity_amount
        ? order?.gratuity_amount?.toString()
        : ''
    )
    : order?.gratuity_percent?.toString();

  const tipExist = Boolean(
    tipValue && '0.00' !== tipValue,
  );

  return tipExist
    ? 'Edit Tip'
    : 'Add Tip';
}

const calculateOrderItemSubtotal = (orderItem) => {
  const itemSubtotal = orderItem.price * orderItem.count;

  let condimentsSubtotal = 0;

  if (
    orderItem.condiments_parsed
    && orderItem.condiments_parsed.length
  ) {
    orderItem.condiments_parsed.forEach((condimentsParsed) => {
      if (
        condimentsParsed.type !== 1
        && !condimentsParsed.checked
      ) {
        return;
      }

      condimentsParsed.items.forEach(
        (condimentsParsedItem) => {
          if (!condimentsParsedItem.checked) {
            return;
          }

          condimentsSubtotal += (
            condimentsParsedItem.price * orderItem.count
          );
        },
      );
    });
  }

  return itemSubtotal + condimentsSubtotal;
}

const calculateItemLevelDiscount = (order) => {
  const isItemDiscountLevel =
    order.department?.discount_level !== 2;

  if (!isItemDiscountLevel) {
    return order.items;
  }

  const isCurrencyItemDiscount =
    order.items_discount_type === 1;

  if (isCurrencyItemDiscount) {
    const orderSubtotal = order.items.reduce(
      (accumulator, orderItem) => {
        return accumulator
          + calculateOrderItemSubtotal(orderItem);
      },
      0,
    );

    order.items.forEach((orderItem) => {
      orderItem.m_discount_type = 1;

      const itemSubtotal =
        calculateOrderItemSubtotal(orderItem);

      orderItem.m_discount_amount = (
        itemSubtotal / orderSubtotal
      ) * order.items_discount_value;
    });

    return order.items;
  }

  order.items.forEach((orderItem) => {
    orderItem.m_discount_type = 2;
    orderItem.m_discount_percentage =
      order.items_discount_value;
  });

  return order.items;
}

export {
  calculateTotalDiscount,
  calculateItemDiscount,
  calculateItemDiscountInAmount,

  calculateTotalItemsDiscount,

  needShowDrinkLimitModal,

  needShowDrinkTimeLimitModal,
  getPayerAlcoholRestrictedDate,

  isTruthyFlag,
  isUpsell,
  getUpsellTitle,
  hasSelectedUpsellCondiment,
  getRequiredCondiments,
  getDisplayedCondiments,

  addItemToOrder,

  getUserSelectType,

  getButtonTipTitle,

  calculateItemLevelDiscount,
}